import { useState } from "react";
import { Outlet, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Info } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import AdBanner from "@/components/AdBanner";
import CreditsModal from "@/components/CreditsModal";
import DeadlineBanner from "@/components/DeadlineBanner";
import PWAInstallBanner from "@/components/PWAInstallBanner";
import Header from "@/components/layout/Header";
import MobileNav from "@/components/layout/MobileNav";
import OnboardingModal from "@/components/OnboardingModal";
import LogoImage from "@/components/LogoImage";
import { useRaceNotifications } from "@/hooks/useRaceNotifications";
import { useBadgeToasts } from "@/hooks/useBadgeToasts";

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [creditsOpen, setCreditsOpen] = useState(false);
  const { data: settings } = useQuery({
    queryKey: ["settings-public"],
    queryFn: async () => (await api.get("/settings/public")).data,
  });
  const branding = settings?.branding || {};

  useRaceNotifications(settings);
  useBadgeToasts();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#121214] flex flex-col">
      <div className="checkered-strip" />
      <Header branding={branding} user={user} onLogout={handleLogout} />

      <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 pt-4">
        <DeadlineBanner />
        <AdBanner slot="header" />
      </div>

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 pb-28 lg:pb-10">
        <Outlet />
      </main>

      <footer className="mt-auto pb-16 lg:pb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-4">
          <AdBanner slot="footer" />
        </div>
        <div className="checkered-strip" />
        <div className="bg-[#0D0D0F] py-4 px-4 flex flex-col sm:flex-row items-center justify-center gap-3">
          <p className="text-center font-body-sport text-[10px] tracking-[0.25em] text-gray-500 uppercase">
            {branding.footer_text || "FantasyRace — Dinamika APS — Chiaramonte Gulfi (RG)"}
          </p>
          {branding.show_sponsor_footer !== false && (branding.footer_logo_url || branding.organizer_logo_url || branding.sponsor_logo_url || (branding.sponsor_logos || []).filter(Boolean).length > 0) && (
            <div
              data-testid="sponsor-footer-grid"
              className="w-full max-w-3xl flex items-center gap-4 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:grid sm:grid-cols-4 lg:grid-cols-6 sm:overflow-visible sm:justify-items-center"
            >
              {branding.footer_logo_url && (
                <LogoImage src={branding.footer_logo_url} alt="Logo footer" className="h-12 max-h-12 w-auto object-contain opacity-80 shrink-0" />
              )}
              {branding.organizer_logo_url && (
                <LogoImage src={branding.organizer_logo_url} alt="Organizzatore" className="h-12 max-h-12 w-auto object-contain opacity-80 shrink-0" />
              )}
              {branding.sponsor_logo_url && (
                <LogoImage src={branding.sponsor_logo_url} alt="Sponsor" className="h-12 max-h-12 w-auto object-contain opacity-80 shrink-0" />
              )}
              {(branding.sponsor_logos || []).filter(Boolean).map((u, i) => (
                <LogoImage key={u} src={u} alt={`Sponsor ${i + 1}`} className="h-12 max-h-12 w-auto object-contain opacity-80 shrink-0" />
              ))}
            </div>
          )}
          <button
            data-testid="credits-button"
            onClick={() => setCreditsOpen(true)}
            className="flex items-center gap-1.5 font-body-sport text-[10px] font-bold tracking-[0.25em] text-gray-500 hover:text-[#FFB703] uppercase transition-colors min-h-[32px]"
          >
            <Info className="w-3.5 h-3.5" /> Crediti App
          </button>
        </div>
        <div className="bg-[#0D0D0F] border-t border-[#3F3F4C] py-3 px-4 text-center space-y-1" data-testid="legal-footer">
          <p className="font-body-sport text-[10px] text-gray-500">
            Dinamika A.P.S. — C.F. 92051380886 — Via G. Marconi n. 38, 97012 Chiaramonte Gulfi (RG) —{" "}
            <a href="mailto:dinamikaaps@gmail.com" className="text-gray-400 hover:text-white transition-colors">
              dinamikaaps@gmail.com
            </a>
          </p>
          <p className="font-body-sport text-[10px] tracking-wider space-x-3">
            <Link to="/privacy" data-testid="footer-privacy-link" className="text-gray-500 hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link to="/cookie-policy" data-testid="footer-cookie-link" className="text-gray-500 hover:text-white transition-colors">
              Cookie Policy
            </Link>
            <Link to="/termini" data-testid="footer-terms-link" className="text-gray-500 hover:text-white transition-colors">
              Termini e Condizioni
            </Link>
            <button
              data-testid="cookie-preferences-button"
              onClick={() => window.dispatchEvent(new Event("fr-open-cookie-preferences"))}
              className="text-gray-500 hover:text-white transition-colors underline underline-offset-2"
            >
              Rivedi le preferenze Cookie
            </button>
          </p>
        </div>
      </footer>

      <CreditsModal open={creditsOpen} onOpenChange={setCreditsOpen} />
      <PWAInstallBanner />
      <OnboardingModal />
      <MobileNav user={user} />
    </div>
  );
}
